import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { api, errText } from "@/lib/api";
import { PROSPECT_STATUSES, PROSPECT_STYLES, timeAgo } from "@/lib/partners";
import { RealtorPerformance } from "@/components/partner/RealtorPerformance";
import { RealtorAssets } from "@/components/partner/RealtorAssets";
import { RealtorOutreach } from "@/components/partner/RealtorOutreach";
import { RealtorLeads } from "@/components/partner/RealtorLeads";
import { RealtorForm } from "@/components/partner/RealtorForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Flame, Archive } from "lucide-react";

export default function RealtorDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);

  const load = useCallback(() => {
    api.get(`/realtors/${id}`).then((res) => setR(res.data)).catch((e) => toast.error(errText(e)));
  }, [id]);

  useEffect(load, [load]);

  const setStatus = async (value) => {
    await api.post("/realtors/bulk", { ids: [id], action: "set_prospect_status", value });
    setR((x) => ({ ...x, prospect_status: value }));
    toast.success(`Status set to ${value}`);
  };

  const toggleArchive = async () => {
    await api.post(`/realtors/${id}/archive`, null, { params: { archived: !r.archived } });
    setR((x) => ({ ...x, archived: !x.archived }));
    toast.success(r.archived ? "Realtor restored" : "Realtor archived");
  };

  if (!r) return <AdminLayout><p className="text-gray-400">Loading...</p></AdminLayout>;

  return (
    <AdminLayout>
      <Link to="/admin/realtors" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-6 transition-colors duration-200">
        <ArrowLeft size={15} /> All realtor partners
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight" data-testid="realtor-detail-name">{r.display_name}</h1>
            {r.hot && (
              <span className="inline-flex items-center gap-1 bg-[#c19a6b] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1" data-testid="realtor-hot-badge">
                <Flame size={11} /> Hot
              </span>
            )}
            <span className={`text-[11px] px-2 py-1 font-semibold tracking-wide ${PROSPECT_STYLES[r.prospect_status] || "bg-gray-100"}`}>{r.prospect_status}</span>
            {r.archived && <span className="text-[11px] bg-gray-200 text-gray-500 px-2 py-1 font-semibold">Archived</span>}
          </div>
          <p className="text-gray-500 mt-2 text-sm">
            {[r.brokerage, r.city, r.email, r.phone].filter(Boolean).join(" · ")}
          </p>
          {r.presentation_views > 0 && (
            <p className="text-sm text-[#8a6a42] mt-2" data-testid="presentation-view-note">
              {r.first_name} viewed the presentation {r.presentation_views} time{r.presentation_views === 1 ? "" : "s"} · last {timeAgo(r.last_viewed_at)}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={r.prospect_status} onValueChange={setStatus}>
            <SelectTrigger data-testid="realtor-status-select" className="w-48 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{PROSPECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="open-presentation-btn" asChild variant="outline" className="h-10 rounded-sm text-[11px] uppercase tracking-wider">
            <a href={r.links.presentation_url} target="_blank" rel="noreferrer"><ExternalLink size={13} className="mr-1.5" /> Presentation</a>
          </Button>
          <Button data-testid="open-client-btn" asChild variant="outline" className="h-10 rounded-sm text-[11px] uppercase tracking-wider">
            <a href={r.links.client_url} target="_blank" rel="noreferrer"><ExternalLink size={13} className="mr-1.5" /> Client Page</a>
          </Button>
          <Button data-testid="archive-realtor-btn" onClick={toggleArchive} variant="outline" className="h-10 rounded-sm text-[11px] uppercase tracking-wider">
            <Archive size={13} className="mr-1.5" /> {r.archived ? "Restore" : "Archive"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="performance">
        <TabsList className="bg-white border border-gray-200 rounded-sm h-auto p-1 flex-wrap justify-start">
          {[
            ["performance", "Performance"],
            ["assets", "Assets"],
            ["outreach", "Outreach"],
            ["leads", `Leads & Quotes${r.leads?.length ? ` (${r.leads.length})` : ""}`],
            ["edit", "Edit"],
          ].map(([v, l]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`tab-${v}`}
              className="rounded-sm text-xs uppercase tracking-[0.15em] data-[state=active]:bg-black data-[state=active]:text-white px-4 py-2.5"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="performance" className="mt-8"><RealtorPerformance realtor={r} /></TabsContent>
        <TabsContent value="assets" className="mt-8"><RealtorAssets realtor={r} programName={r.program_name} /></TabsContent>
        <TabsContent value="outreach" className="mt-8"><RealtorOutreach realtor={r} /></TabsContent>
        <TabsContent value="leads" className="mt-8"><RealtorLeads realtor={r} onChanged={load} /></TabsContent>
        <TabsContent value="edit" className="mt-8"><RealtorForm realtor={r} onSaved={setR} /></TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
